import { apiClient } from './apiClient';
import { captureError } from './core/errors/captureError';

// Full user object as returned by /api/users, including active plan
export type BackendPlan = {
    id: number;
    name: string;
    enabled: boolean;
    price: string;
    currency: string;
    billing_interval: number | null;
    features: string[];
    precedence: number;
    checkout_url?: string;
    trial_period_days: number; // 0 = no trial
    // Capability flags — what this plan unlocks
    grants_education: boolean;
    grants_networking: boolean;
    grants_all_bootcamps: boolean;
    chat_monthly_tokens: number | null; // null = unlimited
};

export type ActivePlan = {
    id: number;
    plan: BackendPlan;
    start_date: string;
    end_date: string | null;
    status: 'active' | 'trialing' | 'cancelled' | 'past_due' | string;
    trial_ends_at: string | null;
    renewal_date: string | null;
};

/** Mentorship role surfaced on the user profile (v1). null = not yet
 * onboarded into the mentorship program. */
export type MentorshipRole = 'mentor' | 'mentee' | 'admin' | null;

export type ModuleKey =
    | 'concept'
    | 'design'
    | 'authorities'
    | 'contract'
    | 'tender'
    | 'construction';

/** Tiny mentor projection inlined on UserProfile when the mentee has an
 * assigned mentor. Mirrors backend ``MentorSummarySerializer``. */
export type MentorSummary = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
};

export type BackendUserWithPlan = {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    profile: {
        id: number;
        background: string;
        focus_areas: string;
        industry_knowledge: number | null;
        desire: string;
        anxieties: string;
        user: number;
        location?: string;
        experience?: string;
        avatar_url?: string;
        // ── Mentorship v1 fields (additive; null/empty when the user has
        // not yet been onboarded into the mentorship program) ──
        role?: MentorshipRole;
        mentor_id?: number | null;
        mentor_summary?: MentorSummary | null;
        mentor_specialties?: ModuleKey[];
        mentor_capacity?: number;
        role_assigned_at?: string | null;
        role_assigned_by_id?: number | null;
    };
    active_plan: ActivePlan | null;
    access: AccessSnapshot | null;
    completeness: { score: number; missing: string[] } | null;
};

export type AccessSnapshot = {
    is_admin: boolean;
    education: boolean;
    networking: boolean;
    allowed_bootcamp_ids: string[];
    grants_all_bootcamps: boolean;
    chat_tokens_limit: number | null;
    chat_tokens_used: number;
    chat_tokens_remaining: number | null;
    period_start: string;
    period_end: string;
    active_plan_id: number | null;
    active_plan_name: string;
    source: 'admin_role' | 'grant' | 'plan' | 'default';
    has_stripe_customer: boolean;
};

export async function fetchCurrentUserWithPlan(): Promise<BackendUserWithPlan | null> {
    try {
        // Adjust path if your backend exposes a different endpoint,
        // e.g. '/api/users/me/' instead of '/api/users/'.
        const response = await apiClient.get<BackendUserWithPlan>('/api/user/');
        return response.data ?? null;
    } catch (err) {
        captureError(err, { scope: 'userService', op: 'fetchCurrentUserWithPlan' });
        return null;
    }
}

export async function fetchBillingPortalUrl(): Promise<string | null> {
    try {
        const response = await apiClient.get<{ url: string }>('/api/user/billing-portal/');
        return response.data.url;
    } catch (err) {
        captureError(err, { scope: 'userService', op: 'fetchBillingPortalUrl' });
        return null;
    }
}

// ── Mentorship role helpers ────────────────────────────────────────────────
// SSOT for role checks across the mentorship UI. Every component that needs
// to branch on role MUST go through these helpers — no inline
// `user?.profile?.role === 'mentor'` checks in components.

/** The user's current mentorship role, or `null` if not yet onboarded. */
export function getMentorshipRole(user: BackendUserWithPlan | null | undefined): MentorshipRole {
    return user?.profile?.role ?? null;
}

export function isMentor(user: BackendUserWithPlan | null | undefined): boolean {
    return getMentorshipRole(user) === 'mentor';
}

export function isMentee(user: BackendUserWithPlan | null | undefined): boolean {
    return getMentorshipRole(user) === 'mentee';
}

export function isAdmin(user: BackendUserWithPlan | null | undefined): boolean {
    // SSOT: defer to the backend access snapshot so both is_staff admins
    // and role='admin' users are handled identically on the client.
    return user?.access?.is_admin ?? false;
}

/** Any mentorship role (mentor, mentee, or admin). Useful for gating
 * the Coaching sub-tab swap away from the role-null course list. */
export function hasMentorshipRole(user: BackendUserWithPlan | null | undefined): boolean {
    const role = getMentorshipRole(user);
    return role === 'mentor' || role === 'mentee' || role === 'admin';
}

/** Returns true when the profile is missing the fields a first-time
 *  user needs to fill to be useful on Networking (avatar + a bio /
 *  background blurb). Drives the Home profile-completion nudge. */
export function isProfileIncomplete(
    user: BackendUserWithPlan | null | undefined,
): boolean {
    if (!user) return false;  // not logged in — don't nudge on public chrome
    const p = user.profile;
    if (!p) return true;
    const hasAvatar = Boolean(p.avatar_url && p.avatar_url.trim());
    const hasBio = Boolean(p.background && p.background.trim().length >= 10);
    return !hasAvatar || !hasBio;
}

/** Resolve the user's primary mentorship-family role, or null if none.
 * SSOT for the 3-way admin|mentor|mentee pick used by Layout.mentalityRole
 * and HomeView.resolvePrimary. Returns `null` for users without any
 * mentorship role so callers can apply their own fallback. */
export function resolveRole(
    user: BackendUserWithPlan | null | undefined,
): 'admin' | 'mentor' | 'mentee' | null {
    if (!user) return null;
    if (isAdmin(user)) return 'admin';
    if (isMentor(user)) return 'mentor';
    if (isMentee(user)) return 'mentee';
    return null;
}

/** Mentor or admin — the "can curate" predicate used by many surfaces
 * (Risk add/edit, Insight curation, ClientProfile CRUD). */
export function canCurate(user: BackendUserWithPlan | null | undefined): boolean {
    return isMentor(user) || isAdmin(user);
}

/** Whether this user can manage their own billing in Settings. False
 * for org-managed accounts once team billing lands — future-proofs
 * the Billing pane so we don't need to re-gate when that ships. */
export function canManageBilling(_user: BackendUserWithPlan | null | undefined): boolean {
    // v1: every individual user can manage billing. Switch this to
    // `!_user?.access?.org_managed_billing` once the backend flag lands.
    return true;
}

// ── Access / paywall helpers ────────────────────────────────────────

function _access(user: BackendUserWithPlan | null | undefined): AccessSnapshot | null {
    return user?.access ?? null;
}

export function hasEducationAccess(user: BackendUserWithPlan | null | undefined): boolean {
    return _access(user)?.education ?? false;
}

export function hasNetworkingAccess(user: BackendUserWithPlan | null | undefined): boolean {
    return _access(user)?.networking ?? false;
}

export function chatQuotaDisplay(user: BackendUserWithPlan | null | undefined): string {
    const a = _access(user);
    if (!a || a.chat_tokens_limit === null) return '';
    const used = a.chat_tokens_used.toLocaleString();
    const limit = a.chat_tokens_limit.toLocaleString();
    return `${used} / ${limit} tokens this month`;
}

export function isChatQuotaExhausted(user: BackendUserWithPlan | null | undefined): boolean {
    const a = _access(user);
    if (!a || a.chat_tokens_remaining === null) return false;
    return a.chat_tokens_remaining <= 0;
}

/**
 * Returns the fraction of chat-token quota consumed (0–1) or null for
 * unlimited / unauthenticated. Drives the pre-warn banner: if the user
 * is ≥80% through their monthly allotment we nudge them before the
 * paywall modal fires on message N+1.
 */
export function chatQuotaFraction(user: BackendUserWithPlan | null | undefined): number | null {
    const a = _access(user);
    if (!a || a.chat_tokens_limit === null || a.chat_tokens_limit === 0) return null;
    return Math.min(1, Math.max(0, a.chat_tokens_used / a.chat_tokens_limit));
}

